import './StatisticsTopic.css';

import axios from 'axios';
import React, {useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useParams} from 'react-router-dom';

import {FetchAllCountries, FetchCountryIso} from './Utils/CountryUtility';
import {RenderChart} from './Utils/StatisticsUtility';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface CurrencyRates {
  [code: string]: number;
}

interface CurrencyConversion {
  target: string;
  rate: string;
}

interface StatisticsRow {
  iso?: string;
  currency?: string;
  exchange_rate_updated_at?: string;
  exchange_rate_to_eur?: string;
  gdp?: number | string;
  cpi?: number | string;
  inflation?: number | string;
  crime?: number | string;
  population?: number | string;
  [key: string]: unknown;
}

interface TourismStatisticsData {
  inbound_tourism?: Record<string, number>;
  outbound_tourism?: Record<string, number>;
  [key: string]: unknown;
}

interface AirlineItem {
  name: string;
  website: string;
  service_type?: string;
  updated_at: string;
  logo_url?: string;
}

interface GroceryStoreItem {
  name: string;
  website: string;
  updated_at: string;
  logo_url?: string;
}

type QueueTask = () => Promise<void>;

let isFetching = false;  // Flag to check if a fetch operation is ongoing
let fetchQueue: QueueTask[] = [];     // Queue to hold fetch functions

// Function to add fetch requests to the queue
function addToFetchQueue(fetchFunction: QueueTask) {
  fetchQueue.push(fetchFunction);

  // If there's no fetch in progress, process the queue
  if (!isFetching) {
    processFetchQueue();
  }
}
// Function to process fetch requests in the queue
async function processFetchQueue() {
  // Continue processing until the queue is empty
  while (fetchQueue.length > 0) {
    isFetching = true;

    // Get the next fetch function in the queue
    const fetchFunction = fetchQueue.shift();
    if (!fetchFunction) {
      isFetching = false;
      continue;
    }

    try {
      // Execute the fetch function
      await fetchFunction();
    } catch (error) {
      console.error('Error during processFetchQueue:', error);
    }

    isFetching = false;
  }
}

async function fetchAllCurrencyEuroRates() {
  try {
    const response = await axios.get<{ rates: CurrencyRates }>(
        `${backendUrl}/worlddata/currency/get-all-currency-eur-rates/`);
    return response.data.rates;
  } catch (error) {
    console.error('Error fetching all currency rates:', error);
    return {};
  }
}

async function fetchGdpCacheData(iso: string) {
  try {
    // Using axios to fetch gdp data
    await axios.get(`${backendUrl}/worlddata/gdp/get-gdp-cache/${iso}`);
  } catch (error) {
    console.error('Error fetching gdp cache data:', error);
  }
}

async function fetchPopulationCacheData(iso: string) {
  try {
    // Using axios to fetch population data
    await axios.get(`${backendUrl}/worlddata/population/get-population-cache/${iso}`);
  } catch (error) {
    console.error('Error fetching population cache data:', error);
  }
}

async function fetchCpiCacheData(iso: string) {
  try {
    // Using axios to fetch cpi data
    await axios.get(`${backendUrl}/worlddata/cpi/get-cpi-cache/${iso}`);
  } catch (error) {
    console.error('Error fetching cpi cache data:', error);
  }
}

async function fetchInflationCacheData(iso: string) {
  try {
    // Using axios to fetch cpi data
    await axios.get(`${backendUrl}/worlddata/inflation/get-inflation-cache/${iso}`);
  } catch (error) {
    console.error('Error fetching inflation cache data:', error);
  }
}

async function fetchCrimeCacheData(iso: string) {
  try {
    // Using axios to fetch cpi data
    await axios.get(`${backendUrl}/worlddata/crime/get-crime-cache/${iso}`);
  } catch (error) {
    console.error('Error fetching crime cache data:', error);
  }
}

async function fetchTourismCacheData(iso: string) {
  try {
    // Using axios to fetch population data
    await axios.get(`${backendUrl}/worlddata/tourism/get-tourism-cache/${iso}`);
  } catch (error) {
    console.error('Error fetching tourism cache data:', error);
  }
}

async function fetchAirlines(iso: string): Promise<AirlineItem[]> {
  try {
    const response = await axios.get(
        `${backendUrl}/worlddata/statistics/get-airline-data/${iso}`);
    return response.data;  // ✅ Return the data instead of setting state
  } catch (error) {
    console.error('Error fetching airline data:', error);
    return [];  // ✅ Return empty array on error
  }
}

async function fetchGroceryStores(iso: string): Promise<GroceryStoreItem[]> {
  try {
    const response = await axios.get(
        `${backendUrl}/worlddata/statistics/get-grocery-store-data/${iso}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching grocery stores:', error);
    return [];
  }
}

function CurrencyConversionTable(
    {currencyConversions, statisticsData}: { currencyConversions: CurrencyConversion[]; statisticsData: StatisticsRow[]; isoList: string[] }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [countryMap, setCountryMap] = useState<Record<string, string[]>>({});

  const baseCurrency = statisticsData[0]?.currency || 'Currency';
  const lastUpdated = statisticsData[0]?.exchange_rate_updated_at ?
      new Date(statisticsData[0].exchange_rate_updated_at)
          .toISOString()
          .split('T')[0] :
      'N/A';

  useEffect(() => {
    async function loadCountryNames() {
      const countries =
          await FetchAllCountries();  // returns [{ name, iso, currency, ... }]
      const map: Record<string, string[]> = {};
      countries.forEach((c) => {
        const currencyValue = typeof c.currency === 'string' ? c.currency : '';
        const code = currencyValue.toUpperCase();
        if (!code) return;
        if (!map[code]) {
          map[code] = [];
        }
        map[code].push(c.name);
      });
      setCountryMap(map);
    }
    loadCountryNames();
  }, []);

  const filteredConversions = currencyConversions.filter((conv) => {
    const search = searchTerm.toLowerCase();
    const key = conv.target.toUpperCase();

    const countries = countryMap[key] || [];

    return conv.target.toLowerCase().includes(search) ||
        countries.some((c) => c.toLowerCase().includes(search));
  });

  return currencyConversions.length > 0 ? (
      <div className='CurrencyCard'>
        <div className='MoneyVisual'>
          <img src={`${backendUrl}/django-static/pictures/generic/money.jpg`} alt='Money illustration' className='MoneyImage' />
        </div>

        <h3 className="CurrencyTitle">Rates for {baseCurrency}</h3>

        <input type='text' placeholder='Search ISO or country' value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className='CurrencySearchInput' />

        <div className='CurrencyTableScroll'>
          <table className='CurrencyTable'>
            <thead>
              <tr>
                <th>Currency</th>
                <th>Rate</th>
              </tr>
            </thead>
            <tbody>
              {filteredConversions.map((conv, index) => (
                <tr key={index}>
                  <td>
                    {conv.target}
                    <div style={{
      fontSize: '10px', color: 'var(--text-muted)' }}>{countryMap[conv.target.toUpperCase()]?.join(', ') || '...'}</div>
                  </td>
                  <td>
                    {conv.rate}
                    <div className='RateTimestamp'>{lastUpdated}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null;
}

// Sequentially call the functions
  function fetchAllCacheData(iso: string, onComplete?: () => void) {
    addToFetchQueue(async () => {
      await fetchGdpCacheData(iso);
    });
    addToFetchQueue(async () => {
      await fetchPopulationCacheData(iso);
    });
    addToFetchQueue(async () => {
      await fetchCpiCacheData(iso);
    });
    addToFetchQueue(async () => {
      await fetchInflationCacheData(iso);
    });
    addToFetchQueue(async () => {
      await fetchCrimeCacheData(iso);
    });
    addToFetchQueue(async () => {
      await fetchTourismCacheData(iso);
    });
    addToFetchQueue(async () => {
      if (onComplete) onComplete();  // ✅ Trigger callback after all fetches
    });
  }

  const StatisticsTopic = () => {
  const { country } = useParams<{ country: string }>(); // Extract the country from the URL

  const [hasExchangeRateToEur, setExchangeRateToEur] = useState(false);
  const [hasGdp, setGdp] = useState(false);
  const [hasCpi, setCpi] = useState(false);
  const [hasInflation, setInflation] = useState(false);
  const [hasCrime, setCrime] = useState(false);
  const [hasPopulation, setPopulation] = useState(false);
  const [statisticsData, setStatisticsData] = useState<StatisticsRow[]>([]); // State to store search results from the database

  const [hasTourism, setTourism] = useState(false);
  const [tourismStatisticsData, setTourismStatisticsData] = useState<TourismStatisticsData | null>(null); // State to store search results from the database

  const [airlineData, setAirlineData] = useState<AirlineItem[]>([]);
  const [groceryStoreData, setGroceryStoreData] = useState<GroceryStoreItem[]>([]);
  const [currencyConversions, setCurrencyConversions] = useState<CurrencyConversion[]>([]);

  const [isoList, setIsoList] = useState<string[]>([]);

  function fetchCachedStatistics(iso: string) {
    axios
      .get(`${backendUrl}/worlddata/statistics/get-statistics-data/${iso}`)
      .then((response) => {
        setStatisticsData(response.data); // Update the search results with matching countries
      })
      .catch((error) => console.error('Error searching countries', error));
  }

  function fetchCachedTourismStatistics(iso: string) {
    axios
      .get(`${backendUrl}/worlddata/statistics/get-tourism-statistics-data/${iso}`)
      .then((response) => {
        setTourismStatisticsData(response.data); // Update the search results with matching countries
      })
      .catch((error) => console.error('Error searching countries', error));
  }

  useEffect(() => {
    FetchAllCountries().then((countries) => {
      const isos = Array.from(new Set([...countries.map((c) => c.iso).filter(Boolean), ...currencyConversions.map((c) => c.target).filter(Boolean)])) as string[];

      setIsoList(isos);
    });
  }, [currencyConversions]);

  useEffect(() => {
    if (statisticsData.length > 0 && statisticsData[0].currency) {
      fetchAllCurrencyEuroRates().then((rates) => {
        const base = statisticsData[0].currency ?? '';
        if (!base) {
          setCurrencyConversions([]);
          return;
        }
        const baseRate = rates[base] ?? 1;
        const conversions = Object.entries(rates)
          .filter(([target]) => target !== base)
          .map(([target, targetRate]) => ({
            target,
            rate: ((1 / baseRate) * Number(targetRate)).toFixed(4),
          }));
        setCurrencyConversions(conversions);
      });
    }
  }, [statisticsData]);

  useEffect(() => {
    if (!country) {
      return;
    }
    FetchCountryIso(country)
      .then((iso) => {
        if (!iso) {
          return;
        }
        fetchCachedStatistics(iso);
        fetchCachedTourismStatistics(iso);

        // ✅ Re-fetch both after cache updates
        fetchAllCacheData(iso, () => {
          fetchCachedStatistics(iso);
          fetchCachedTourismStatistics(iso);
        });
      })
      .catch((error) => {
        console.error('Error fetching country iso:', error);
      });
  }, [country]);

  useEffect(() => {
    if (!country) {
      return;
    }
    FetchCountryIso(country)
      .then(async (iso) => {
        if (!iso) {
          return;
        }
        setGroceryStoreData(await fetchGroceryStores(iso));
        setAirlineData(await fetchAirlines(iso));
      })
      .catch((error) => {
        console.error('Error fetching country iso:', error);
      });
  }, [country]);

  useEffect(() => {
    if (statisticsData.length > 0) {
      setExchangeRateToEur(statisticsData[0].exchange_rate_to_eur !== 'None');
      setGdp(statisticsData[0].gdp !== 'None');
      setCpi(statisticsData[0].cpi !== 'None');
      setCrime(statisticsData[0].crime !== 'None');
      setInflation(statisticsData[0].inflation !== 'None');
      setPopulation(statisticsData[0].population !== 'None');
    }

    if (tourismStatisticsData) {
      setTourism(Boolean(tourismStatisticsData.inbound_tourism && Object.keys(tourismStatisticsData.inbound_tourism).length > 0));
    }
  }, [statisticsData, tourismStatisticsData]);

  //Render the charts
  useEffect(() => {
    if (hasTourism && tourismStatisticsData) {
      if (tourismStatisticsData.inbound_tourism) {
        RenderChart(tourismStatisticsData.inbound_tourism, 'Inbound Tourism', 'InboundTourismChart');
      }

      if (tourismStatisticsData.outbound_tourism) {
        RenderChart(tourismStatisticsData.outbound_tourism, 'Outbound Tourism', 'OutboundTourismChart');
      }
    }
  }, [hasTourism, tourismStatisticsData]);

  const encodedCountry = encodeURIComponent(country || '');
  const canonicalUrl = `https://livingoutofabackpack.com/traveltopics/${encodedCountry}/topics/statistics`;
  const countryIsoImage = statisticsData?.[0]?.iso;
  const socialImage = countryIsoImage
      ? `https://flagcdn.com/w320/${String(countryIsoImage).toLowerCase()}.png`
      : 'https://livingoutofabackpack.com/static/logo.jpg';

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://livingoutofabackpack.com/home/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Travel Topics',
        item: 'https://livingoutofabackpack.com/traveltopics/',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${country} Statistics`,
        item: canonicalUrl,
      },
    ],
  };

  return (
    <div>
      <div className='StatisticsPage'>
        <Helmet>
          <title>{country} Travel Statistics | Currency, GDP & Tourism Data</title>
          <link rel="canonical" href={canonicalUrl} />
          <meta name="robots" content="index, follow" />
          <meta name="description" content={`Explore ${country} travel statistics including currency conversion context, GDP, tourism, and key planning data.`} />
          <meta property="og:title" content={`${country} Travel Statistics | Currency, GDP & Tourism Data`} />
          <meta property="og:description" content={`Review practical travel data for ${country}, including economy and tourism indicators.`} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:image" content={socialImage} />
          <meta property="og:image:alt" content={`Statistics overview for ${country}`} />
          <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        </Helmet>

        <div className="Title"> Statistics - {country} 📈</div>

        <div className="StatisticsGrid">
          {hasExchangeRateToEur && (
            <div className="CurrencyBlock">
              <CurrencyConversionTable statisticsData={statisticsData} currencyConversions={currencyConversions} isoList={isoList} />
            </div>
          )}

          {hasGdp && (
            <div className="GdpBlock">
              <div className="TextContainer">
                <h2>
                  <strong>GDP in dollars</strong>
                  <br />
                  <span className="material-icons">{'money'}</span>
                </h2>
              </div>
              <h2 style={{ color: 'black', alignSelf: 'center', fontSize: '14px' }}>
                {statisticsData[0].gdp} <strong>$</strong>
                <br />
                {(Number(statisticsData[0].gdp ?? 0) / 1_000_000_000).toFixed(2)} <strong>Billion $</strong>
                {/* Footnote */}
                <div
                  className="StatisticsAttribution"
                  style={{
                    fontSize: '10px',
                    color: 'var(--text-faint)',
                    marginTop: '6px',
                    textAlign: 'center',
                  }}
                >
                  Source: worldbank.org
                </div>
              </h2>
            </div>
          )}

          {hasPopulation && (
            <div className="PopulationBlock">
              <div className="TextContainer">
                <h2>
                  <strong>Population</strong> <br />
                  <span className="material-icons">{'groups'}</span>
                </h2>
              </div>
              <h2 style={{ color: 'black', alignSelf: 'center', fontSize: '14px', margin: 'auto' }}>
                {statisticsData[0].population} <span className="material-icons">{'person'}</span>
                <br />
                {(Number(statisticsData[0].population ?? 0) / 1_000_000).toFixed(2)} <strong>Million</strong>
                {/* Footnote */}
                <div
                  className="StatisticsAttribution"
                  style={{
                    fontSize: '10px',
                    color: 'var(--text-faint)',
                    marginTop: '6px',
                    textAlign: 'center',
                  }}
                >
                  Source: worldbank.org
                </div>
              </h2>
            </div>
          )}

          {hasCpi && (
            <div className="CpiBlock">
              <div className="TextContainer">
                <h2>
                  <strong>CPI</strong> <br />
                  <span className="material-icons">{'price_change'}</span>
                </h2>
              </div>

              <h2
                style={{
                  color: 'black',
                  alignSelf: 'center',
                  fontSize: '14px',
                  margin: 'auto',
                }}
              >
                {statisticsData[0].cpi}
                <span className="material-icons">{'trending_up'}</span>
                <br />
                <strong>Consumer Price Index</strong>
                <br />
                <span style={{ fontSize: '11px', color: 'var(--text-subtle)' }}>Higher CPI means everyday goods and services are more expensive.</span>

                {/* Footnote */}
                <div
                  className="StatisticsAttribution"
                  style={{
                    fontSize: '10px',
                    color: 'var(--text-faint)',
                    marginTop: '6px',
                    textAlign: 'center',
                  }}
                >
                  Source: worldbank.org
                </div>
              </h2>
            </div>
          )}

          {hasInflation && (
            <div className="InflationBlock">
              <div className="TextContainer">
                <h2>
                  <strong>Inflation</strong> <br />
                  <span className="material-icons">{'trending_up'}</span>
                </h2>
              </div>

              <h2
                style={{
                  color: 'black',
                  alignSelf: 'center',
                  fontSize: '14px',
                  margin: 'auto',
                }}
              >
                {statisticsData[0].inflation}%<span className="material-icons">{'show_chart'}</span>
                <br />
                <strong>Inflation Rate</strong>
                <br />
                <span style={{ fontSize: '11px', color: 'var(--text-subtle)' }}>Higher inflation means prices are rising faster, making travel more expensive over time.</span>
                {/* Footnote */}
                <div
                  className="StatisticsAttribution"
                  style={{
                    fontSize: '10px',
                    color: 'var(--text-faint)',
                    marginTop: '6px',
                    textAlign: 'center',
                  }}
                >
                  Source: worldbank.org
                </div>
              </h2>
            </div>
          )}

          {hasCrime && (
            <div className="CrimeBlock">
              <div className="TextContainer">
                <h2>
                  <strong>Crime Rate</strong> <br />
                  <span className="material-icons">{'gavel'}</span>
                </h2>
              </div>

              <h2
                style={{
                  color: 'black',
                  alignSelf: 'center',
                  fontSize: '14px',
                  margin: 'auto',
                }}
              >
                {statisticsData[0].crime}
                <span className="material-icons">{'warning'}</span>
                <br />
                <strong>Homicides per 100,000 people</strong>
                <br />
                <span style={{ fontSize: '11px', color: 'var(--text-subtle)' }}>Higher values indicate a less safe environment for travelers.</span>

                {/* Footnote */}
                <div
                  className="StatisticsAttribution"
                  style={{
                    fontSize: '10px',
                    color: 'var(--text-faint)',
                    marginTop: '6px',
                    textAlign: 'center',
                  }}
                >
                  Source: worldbank.org
                </div>
              </h2>
            </div>
          )}

          {hasTourism && (
            <div className="TourismBlock">
              <canvas id="InboundTourismChart" width="600" height="400"></canvas>

              <canvas id="OutboundTourismChart" width="600" height="400"></canvas>
              {/* Footnote */}
              <div
                className="StatisticsAttribution"
                style={{
                  fontSize: '10px',
                  color: 'var(--text-faint)',
                  marginTop: '6px',
                  textAlign: 'center',
                }}
              >
                Source: worldbank.org
              </div>
            </div>
          )}

          <div className="MiscellaneousStatisticsGrid">
            {airlineData.length > 0 && (
              <div className="AirlineList">
                <h2 style={{ color: 'var(--text-color)' }}>Airlines Based in {country}</h2>
                <div className="AirlineContent">
                  <div className="ScrollableAirlineList">
                    <ul className="AirlineListItems">
                      {airlineData.map((airline, index) => (
                        <li key={index} className="AirlineItem">
                          <a href={airline.website} target="_blank" rel="noopener noreferrer" className="AirlineLinkWrapper">
                            <div className="AirlineText">
                              ✈️ {airline.name}
                              <span className="ServiceType">({airline.service_type})</span>
                              <div className="UpdatedLabel">
                                <small style={{ fontSize: '8px', color: 'var(--primary-color)' }}>Updated: {new Date(airline.updated_at).toISOString().split('T')[0]}</small>
                              </div>
                            </div>

                            {/* Show logo if it exists */}
                            {airline.logo_url && (
                              <div className="AirlineLogo">
                                <img
                                  src={`${backendUrl}/django-dynamic/pictures/${airline.logo_url}`}
                                  alt={`${airline.name} logo`}
                                  style={{ width: '40px', height: '40px', objectFit: 'contain', marginTop: '4px' }}
                                />
                              </div>
                            )}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
                    <img src={`${backendUrl}/django-static/pictures/generic/airplane-taking-off.jpg`} alt="Airplane" className="AirlineSideImage" />

                    <div className="LogoDisclaimer">
                      <small>
                        Disclaimer: All logos displayed on this page are the property of their respective owners. They are used here for informational and identification purposes. This website is not
                        affiliated with, sponsored by, or endorsed by any of the listed companies.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {groceryStoreData.length > 0 && (
              <div className="GroceryStoreList">
                <h2 style={{ color: 'var(--text-color)' }}>Grocery Stores in {country}</h2>
                <div className="GroceryStoreContent">
                  <div className="ScrollableGroceryList">
                    <ul className="GroceryStoreListItems">
                      {groceryStoreData.map((store, index) => (
                        <li key={index} className="GroceryStoreItem">
                          <a href={store.website} target="_blank" rel="noopener noreferrer" className="GroceryStoreLinkWrapper">
                            <div className="GroceryStoreText">
                              🛒 {store.name}
                              <div className="UpdatedLabel">
                                <small style={{ fontSize: '8px', color: 'var(--primary-color)' }}>Updated: {new Date(store.updated_at).toISOString().split('T')[0]}</small>
                              </div>
                            </div>

                            {/* Show logo if it exists */}
                            {store.logo_url && (
                              <div className="StoreLogo">
                                <img
                                  src={`${backendUrl}/django-dynamic/pictures/${store.logo_url}`}
                                  alt={`${store.name} logo`}
                                  style={{ width: '40px', height: '40px', objectFit: 'contain', marginTop: '4px' }}
                                />
                              </div>
                            )}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="GroceryTab" style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
                    <img src={`${backendUrl}/django-static/pictures/generic/grocery-store.jpg`} alt="Grocery Store" className="GroceryStoreSideImage" />

                    <div className="LogoDisclaimer">
                      <small>
                        Disclaimer: All logos displayed on this page are the property of their respective owners. They are used here for informational and identification purposes. This website is not
                        affiliated with, sponsored by, or endorsed by any of the listed companies.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsTopic;
